import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
// import { Button } from '@/components/ui/button';
import { LogOut, User, ShoppingBasket, Store, Settings, Menu, X, Bell } from 'lucide-react';

const Navbar = () => {
  const { user, logout, siteSettings, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const withAuth = () => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  };

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      withAuth();
      const res = await axios.get(`${API}/notifications/my`);
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // silent
    }
  };

  const markNotificationRead = async (notificationId) => {
    if (!user || !notificationId) return;
    try {
      withAuth();
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
    } catch (e) {
      // silent
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.is_read);
    if (unread.length === 0) return;

    // best-effort: fire requests in parallel
    withAuth();
    await Promise.allSettled(unread.map(n => axios.put(`${API}/notifications/${n.id}/read`)));
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  useEffect(() => {
    fetchNotifications();
    if (!user) return;
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleLogout = () => {
    logout();
    navigate('/marketplace');
    setIsMobileMenuOpen(false); // Close mobile menu after logout
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg border-b border-green-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            {siteSettings?.use_custom_logo && siteSettings?.site_logo_url ? (
              <img 
                src={`${siteSettings.site_logo_url}`}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-orange-500 rounded-full flex items-center justify-center">
                <ShoppingBasket className="w-6 h-6 text-white" />
              </div>
            )}
            <span className="text-2xl font-bold text-green-600">
              {siteSettings?.site_name || 'Nossa Feira'}
            </span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-4">
            <Link 
              to="/marketplace" 
              className="text-gray-700 hover:text-green-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Marketplace
            </Link>
            
            {user && user.user_type === 'produtor' && (
              <Link 
                to="/produtor" 
                className="text-gray-700 hover:text-green-600 px-3 py-2 rounded-md text-sm font-medium transition-colors relative"
              >
                Meus Produtos
                {/* Indicator for rejected products (you can add state management here) */}
              </Link>
            )}
            
            {user && user.role === 'admin' && (
              <Link 
                to="/admin" 
                className="text-gray-700 hover:text-green-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Admin
              </Link>
            )}
          </div>

          {/* User Actions - Hidden on mobile */}
          <div className="hidden md:flex items-center space-x-3">
            {user && (
              <div className="relative">
                <button
                  onClick={async () => {
                    const willOpen = !notifOpen;
                    setNotifOpen(willOpen);
                    if (willOpen) {
                      await fetchNotifications();
                      // mark all as read when opening the panel
                      await markAllRead();
                    }
                  }}
                  className="relative px-2 py-1 border border-gray-300 rounded text-sm text-gray-600 hover:text-green-600 hover:border-green-300 transition-colors"
                  title="Notificações"
                >
                  <Bell className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-1 min-w-[18px] h-[18px] flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded shadow-lg z-50">
                    <div className="p-3 border-b flex items-center justify-between">
                      <span className="font-medium text-gray-800">Notificações</span>
                      <button
                        className="text-xs text-gray-500 hover:text-gray-700"
                        onClick={() => setNotifOpen(false)}
                      >
                        Fechar
                      </button>
                    </div>
                    <div className="max-h-80 overflow-auto">
                      {notifications.length === 0 ? (
                        <div className="p-3 text-sm text-gray-600">Nenhuma notificação.</div>
                      ) : (
                        notifications.map((n) => (
                          <button
                            key={n.id}
                            onClick={() => markNotificationRead(n.id)}
                            className={`w-full text-left p-3 border-b last:border-b-0 hover:bg-green-50 ${n.is_read ? 'bg-white' : 'bg-green-50'}`}
                          >
                            <div className="text-sm font-semibold text-gray-800">{n.title}</div>
                            <div className="text-sm text-gray-600 mt-1">{n.message}</div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {user ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 text-sm">
                  <User className="w-4 h-4 text-green-600" />
                  <span className="text-gray-700 font-medium">{user.name}</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    {user.user_type === 'produtor' ? 'Produtor' : user.role === 'admin' ? 'Admin' : 'Consumidor'}
                  </span>
                </div>
                <Link
                  to="/profile"
                  className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-600 hover:text-green-600 hover:border-green-300 transition-colors"
                >
                  Perfil
                </Link>
                <button 
                  onClick={handleLogout}
                  className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-600 hover:text-red-600 hover:border-red-300"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
                    Entrar
                  </button>
                </Link>
                <Link to="/register">
                  <button className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm">
                    Cadastrar
                  </button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-700 hover:text-green-600 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200 shadow-lg">
              {/* Navigation Links */}
              <Link
                to="/marketplace"
                onClick={closeMobileMenu}
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors"
              >
                Marketplace
              </Link>

              {user && user.user_type === 'produtor' && (
                <Link
                  to="/produtor"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors"
                >
                  Meus Produtos
                </Link>
              )}

              {user && user.role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors"
                >
                  Admin
                </Link>
              )}

              {/* User Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                {user ? (
                  <div className="space-y-3">
                    {/* User Info */}
                    <div className="px-3 py-2">
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="w-4 h-4 text-green-600" />
                        <span className="text-gray-700 font-medium">{user.name}</span>
                      </div>
                      <span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        {user.user_type === 'produtor' ? 'Produtor' : user.role === 'admin' ? 'Admin' : 'Consumidor'}
                      </span>
                    </div>

                    {/* Profile Link */}
                    <Link
                      to="/profile"
                      onClick={closeMobileMenu}
                      className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 transition-colors flex items-center space-x-2"
                    >
                      <User className="w-4 h-4" />
                      <span>Meu Perfil</span>
                    </Link>

                    {/* Logout Button */}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sair</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 px-3">
                    <Link
                      to="/login"
                      onClick={closeMobileMenu}
                      className="block w-full text-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Entrar
                    </Link>
                    <Link
                      to="/register"
                      onClick={closeMobileMenu}
                      className="block w-full text-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md text-sm font-medium transition-colors"
                    >
                      Cadastrar
                    </Link>
                  </div>
                )}
              </div>

              {/* Footer Links */}
              <div className="border-t border-gray-200 pt-4 mt-4 space-y-1">
                <Link
                  to="/sobre"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 rounded-md text-sm text-gray-500 hover:text-green-600 hover:bg-green-50 transition-colors"
                >
                  Quem Somos
                </Link>
                <Link
                  to="/ajuda"
                  onClick={closeMobileMenu}
                  className="block px-3 py-2 rounded-md text-sm text-gray-500 hover:text-green-600 hover:bg-green-50 transition-colors"
                >
                  Central de Ajuda
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;